/**
 * FormBuilder Component
 *
 * Custom form builder for creating surveys without drag-and-drop.
 * Provides a simple interface for adding, editing, and reordering questions.
 */

import React, { useState, useCallback } from 'react'
import QuestionEditor from './QuestionEditor'
import QuestionPreview from './QuestionPreview'
import { getDefaultDialCode } from '../../constants/countryCodes'
import './FormBuilder.css'

// Curated colour palette for global question backgrounds
const GLOBAL_COLOR_SWATCHES = [
  { label: 'Default', value: '' },
  { label: 'Ice Blue', value: '#eff6ff' },
  { label: 'Lavender', value: '#f3f0ff' },
  { label: 'Mint', value: '#ecfdf5' },
  { label: 'Peach', value: '#fff7ed' },
  { label: 'Rose', value: '#fff1f2' },
  { label: 'Lemon', value: '#fefce8' },
  { label: 'Slate', value: '#f1f5f9' },
  { label: 'Lilac', value: '#faf5ff' },
]

// Layout element types (non-question)
const LAYOUT_TYPES = ['section_header', 'divider']

// Question type definitions - sorted by popularity
const QUESTION_TYPES = [
  { type: 'radio', label: 'Single Choice', icon: '🔘' },
  { type: 'checkbox', label: 'Multiple Choice', icon: '☑️' },
  { type: 'rating', label: 'Rating', icon: '⭐' },
  { type: 'text', label: 'Text Input', icon: '📝' },
  { type: 'textarea', label: 'Long Text', icon: '📄' },
  { type: 'select', label: 'Dropdown', icon: '📋' },
  { type: 'email', label: 'Email', icon: '📧' },
  { type: 'number', label: 'Number', icon: '🔢' },
  { type: 'phone', label: 'Phone Number', icon: '📱' },
  { type: 'open_response', label: 'Open Response', icon: '💬', isMedia: true },
  { type: 'audio', label: 'Audio Recording', icon: '🎤', isMedia: true },
  { type: 'video', label: 'Video Recording', icon: '📹', isMedia: true },
  { type: 'image', label: 'Image/Photo', icon: '📷', isMedia: true },
  // Layout elements (non-question)
  { type: 'section_header', label: 'Section Header', icon: '📌', isLayout: true },
  { type: 'divider', label: 'Divider', icon: '➖', isLayout: true },
]

// Available languages for survey
const AVAILABLE_LANGUAGES = [
  { code: 'en', name: 'English', dir: 'ltr' },
  { code: 'hi', name: 'हिन्दी (Hindi)', dir: 'ltr' },
  { code: 'ta', name: 'தமிழ் (Tamil)', dir: 'ltr' },
  { code: 'te', name: 'తెలుగు (Telugu)', dir: 'ltr' },
  { code: 'kn', name: 'ಕನ್ನಡ (Kannada)', dir: 'ltr' },
  { code: 'ml', name: 'മലയാളം (Malayalam)', dir: 'ltr' },
  { code: 'mr', name: 'मराठी (Marathi)', dir: 'ltr' },
  { code: 'gu', name: 'ગુજરાતી (Gujarati)', dir: 'ltr' },
  { code: 'bn', name: 'বাংলা (Bengali)', dir: 'ltr' },
  { code: 'pa', name: 'ਪੰਜਾਬੀ (Punjabi)', dir: 'ltr' },
  { code: 'ar', name: 'العربية (Arabic)', dir: 'rtl' },
  { code: 'ur', name: 'اردو (Urdu)', dir: 'rtl' },
  { code: 'es', name: 'Español (Spanish)', dir: 'ltr' },
  { code: 'fr', name: 'Français (French)', dir: 'ltr' },
  { code: 'de', name: 'Deutsch (German)', dir: 'ltr' },
  { code: 'pt', name: 'Português (Portuguese)', dir: 'ltr' },
  { code: 'zh', name: '中文 (Chinese)', dir: 'ltr' },
  { code: 'ja', name: '日本語 (Japanese)', dir: 'ltr' },
  { code: 'ko', name: '한국어 (Korean)', dir: 'ltr' },
]

// Media response types
const RESPONSE_TYPES = [
  { type: 'text', label: 'Text', default: true },
  { type: 'audio', label: 'Audio' },
  { type: 'video', label: 'Video' },
  { type: 'image', label: 'Image' },
]

// Generate unique ID
const generateId = () => `q_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

// Default question properties by type
const getDefaultQuestion = (type) => {
  const base = {
    id: generateId(),
    type,
    label: '',
    required: false,
  }

  switch (type) {
    case 'text':
    case 'email':
      return { ...base, placeholder: '' }
    case 'textarea':
      return { ...base, placeholder: '', rows: 4 }
    case 'number':
      return { ...base, placeholder: '', min: null, max: null }
    case 'phone':
      return { ...base, placeholder: '', countryCode: getDefaultDialCode() }
    case 'rating':
      return { ...base, maxRating: 5, ratingStyle: 'stars' }
    case 'select':
    case 'radio':
    case 'checkbox':
      return { ...base, options: ['', ''] }
    case 'audio':
      return { ...base, maxDurationMs: 300000 } // 5 minutes default
    case 'video':
      return { ...base, maxDurationMs: 120000 } // 2 minutes default
    case 'open_response':
      return { ...base }
    case 'image':
      return { ...base, allowUpload: true }
    case 'section_header':
      return { ...base, label: '', description: '', required: false }
    case 'divider':
      return { ...base, label: '', required: false }
    default:
      return base
  }
}

export default function FormBuilder({
  initialForm,
  onSave,
  onSaveDraft,
  saving,
  isEditing,
  brandImageUrl,
  surveyId,
}) {
  const [form, setForm] = useState(
    initialForm || {
      title: '',
      description: '',
      questions: [],
      // Language settings
      default_language: 'en',
      text_direction: 'ltr',
      available_languages: ['en'],
      // Media response settings
      allowed_response_types: ['text'],
    }
  )
  const [selectedQuestionId, setSelectedQuestionId] = useState(null)
  const [showAddMenu, setShowAddMenu] = useState(false)
  const [showSettings, setShowSettings] = useState(false)

  // Get selected question
  const selectedQuestion = form.questions.find((q) => q.id === selectedQuestionId)

  // Add a new question
  const addQuestion = useCallback((type) => {
    const newQuestion = getDefaultQuestion(type)
    // Leave label empty so user can type immediately

    setForm((prev) => {
      const updates = {
        ...prev,
        questions: [...prev.questions, newQuestion],
      }

      // Auto-enable media response types when adding media/open_response questions
      // so the user doesn't have to manually toggle them in Settings
      if (['audio', 'video', 'image', 'open_response'].includes(type)) {
        const types = new Set(prev.allowed_response_types || ['text'])
        if (type === 'audio' || type === 'open_response') types.add('audio')
        if (type === 'image' || type === 'open_response') types.add('image')
        if (type === 'video') types.add('video')
        updates.allowed_response_types = [...types]
      }

      return updates
    })
    setSelectedQuestionId(newQuestion.id)
    setShowAddMenu(false)
  }, [])

  // Update a question
  const updateQuestion = useCallback((questionId, updates) => {
    setForm((prev) => ({
      ...prev,
      questions: prev.questions.map((q) => (q.id === questionId ? { ...q, ...updates } : q)),
    }))
  }, [])

  // Delete a question
  const deleteQuestion = useCallback(
    (questionId) => {
      setForm((prev) => ({
        ...prev,
        questions: prev.questions.filter((q) => q.id !== questionId),
      }))
      if (selectedQuestionId === questionId) {
        setSelectedQuestionId(null)
      }
    },
    [selectedQuestionId]
  )

  // Move question up/down
  const moveQuestion = useCallback((questionId, direction) => {
    setForm((prev) => {
      const questions = [...prev.questions]
      const index = questions.findIndex((q) => q.id === questionId)
      if (index === -1) return prev

      const newIndex = direction === 'up' ? index - 1 : index + 1
      if (newIndex < 0 || newIndex >= questions.length)
        return prev

        // Swap questions
      ;[questions[index], questions[newIndex]] = [questions[newIndex], questions[index]]
      return { ...prev, questions }
    })
  }, [])

  // Duplicate a question
  const duplicateQuestion = useCallback((questionId) => {
    setForm((prev) => {
      const question = prev.questions.find((q) => q.id === questionId)
      if (!question) return prev

      const newQuestion = {
        ...question,
        id: generateId(),
        label: `${question.label} (copy)`,
      }

      const index = prev.questions.findIndex((q) => q.id === questionId)
      const questions = [...prev.questions]
      questions.splice(index + 1, 0, newQuestion)

      return { ...prev, questions }
    })
  }, [])

  // Update form metadata
  const updateFormMeta = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }))
  }, [])

  // Toggle language in available languages
  const toggleAvailableLanguage = useCallback((langCode) => {
    setForm((prev) => {
      const available = prev.available_languages || ['en']
      if (available.includes(langCode)) {
        // Don't remove if it's the only one or the default
        if (available.length <= 1 || langCode === prev.default_language) {
          return prev
        }
        return { ...prev, available_languages: available.filter((l) => l !== langCode) }
      } else {
        return { ...prev, available_languages: [...available, langCode] }
      }
    })
  }, [])

  // Toggle response type
  const toggleResponseType = useCallback((type) => {
    setForm((prev) => {
      const types = prev.allowed_response_types || ['text']
      if (types.includes(type)) {
        // Don't remove text type if it's the only one
        if (types.length <= 1) {
          return prev
        }
        return { ...prev, allowed_response_types: types.filter((t) => t !== type) }
      } else {
        return { ...prev, allowed_response_types: [...types, type] }
      }
    })
  }, [])

  // Update default language and adjust text direction
  const updateDefaultLanguage = useCallback((langCode) => {
    const lang = AVAILABLE_LANGUAGES.find((l) => l.code === langCode)
    setForm((prev) => {
      // Add to available languages if not present
      const available = prev.available_languages || []
      const newAvailable = available.includes(langCode) ? available : [...available, langCode]
      return {
        ...prev,
        default_language: langCode,
        text_direction: lang?.dir || 'ltr',
        available_languages: newAvailable,
      }
    })
  }, [])

  // Handle save & publish
  const handleSave = useCallback(() => {
    if (onSave) {
      onSave(form)
    }
  }, [form, onSave])

  // Handle save as draft
  const handleSaveDraft = useCallback(() => {
    if (onSaveDraft) {
      onSaveDraft(form)
    }
  }, [form, onSaveDraft])

  // Check if form has media questions
  const hasMediaQuestions = form.questions.some((q) =>
    ['audio', 'video', 'image', 'open_response'].includes(q.type)
  )

  return (
    <div className="form-builder">
      {/* Form Header */}
      <div className="form-builder-header">
        <div className="form-meta">
          <input
            type="text"
            className="form-title-input"
            placeholder="Survey Title"
            value={form.title}
            onChange={(e) => updateFormMeta('title', e.target.value)}
          />
          <input
            type="text"
            className="form-description-input"
            placeholder="Survey description (optional)"
            value={form.description}
            onChange={(e) => updateFormMeta('description', e.target.value)}
          />
        </div>
        <div className="header-actions">
          <button
            className="btn btn-secondary settings-btn"
            onClick={() => setShowSettings(!showSettings)}
            title="Survey Settings">
            ⚙️ Settings
          </button>
          {onSaveDraft && !isEditing && (
            <button
              className="btn btn-secondary save-draft-btn"
              onClick={handleSaveDraft}
              disabled={saving}
              style={{ marginRight: '0.5rem' }}>
              {saving ? 'Saving...' : 'Save as Draft'}
            </button>
          )}
          <button className="btn btn-primary save-btn" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : isEditing ? 'Save Changes' : 'Save & Publish'}
          </button>
        </div>
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <div className="settings-panel">
          <div className="settings-section">
            <h4>Language Settings</h4>
            <div className="form-group">
              <label htmlFor="default-lang">Default Language</label>
              <select
                id="default-lang"
                value={form.default_language || 'en'}
                onChange={(e) => updateDefaultLanguage(e.target.value)}>
                {AVAILABLE_LANGUAGES.map((lang) => (
                  <option key={lang.code} value={lang.code}>
                    {lang.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="text-dir">Text Direction</label>
              <select
                id="text-dir"
                value={form.text_direction || 'ltr'}
                onChange={(e) => updateFormMeta('text_direction', e.target.value)}>
                <option value="ltr">Left to Right (LTR)</option>
                <option value="rtl">Right to Left (RTL)</option>
              </select>
            </div>
            <div className="form-group">
              <label>Available Languages (for multilingual surveys)</label>
              <div className="checkbox-grid">
                {AVAILABLE_LANGUAGES.slice(0, 10).map((lang) => (
                  <label key={lang.code} className="checkbox-item">
                    <input
                      type="checkbox"
                      checked={(form.available_languages || []).includes(lang.code)}
                      onChange={() => toggleAvailableLanguage(lang.code)}
                      disabled={lang.code === form.default_language}
                    />
                    <span>{lang.code.toUpperCase()}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="settings-section">
            <h4>Appearance</h4>
            <div className="form-group">
              <label>Question Background Colour</label>
              <p className="settings-hint" style={{ marginBottom: '0.5rem' }}>
                Applies to all questions. Individual questions can override this.
              </p>
              <div className="swatch-grid">
                {GLOBAL_COLOR_SWATCHES.map((sw) => {
                  const isActive = (form.questionBackgroundColor || '') === sw.value
                  return (
                    <button
                      key={sw.value || 'default'}
                      type="button"
                      className={`swatch-btn ${isActive ? 'active' : ''}`}
                      title={sw.label}
                      onClick={() => updateFormMeta('questionBackgroundColor', sw.value)}>
                      <span
                        className="swatch-circle"
                        style={{ background: sw.value || '#f7fafc' }}
                      />
                      {sw.value === '' && <span className="swatch-slash" />}
                    </button>
                  )
                })}
              </div>
            </div>
          </div>

          <div className="settings-section">
            <h4>Response Types</h4>
            <p className="settings-hint">Enable media response types for your survey</p>
            <div className="checkbox-grid">
              {RESPONSE_TYPES.map((rt) => (
                <label key={rt.type} className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={(form.allowed_response_types || ['text']).includes(rt.type)}
                    onChange={() => toggleResponseType(rt.type)}
                  />
                  <span>{rt.label}</span>
                </label>
              ))}
            </div>
            {hasMediaQuestions &&
              !form.allowed_response_types?.some((t) =>
                ['audio', 'video', 'image'].includes(t)
              ) && (
                <p className="settings-warning">
                  ⚠️ Your survey has media questions but media response types are not enabled.
                </p>
              )}
          </div>
        </div>
      )}

      <div className="form-builder-content">
        {/* Questions List */}
        <div className="questions-panel">
          <div className="panel-header">
            <h3>Questions</h3>
            <div className="add-question-wrapper">
              <button className="btn btn-add" onClick={() => setShowAddMenu(!showAddMenu)}>
                + Add Question
              </button>
              {showAddMenu && (
                <div className="add-question-menu">
                  {QUESTION_TYPES.filter((qt) => !qt.isLayout).map((qt) => (
                    <button
                      key={qt.type}
                      className="add-question-option"
                      onClick={() => addQuestion(qt.type)}>
                      <span className="qt-icon">{qt.icon}</span>
                      <span>{qt.label}</span>
                    </button>
                  ))}
                  <div className="add-menu-separator">Layout</div>
                  {QUESTION_TYPES.filter((qt) => qt.isLayout).map((qt) => (
                    <button
                      key={qt.type}
                      className="add-question-option"
                      onClick={() => addQuestion(qt.type)}>
                      <span className="qt-icon">{qt.icon}</span>
                      <span>{qt.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="questions-list">
            {form.questions.length === 0 ? (
              <div className="empty-questions">
                <p>No questions yet</p>
                <p className="hint">Click "Add Question" to get started</p>
              </div>
            ) : (
              form.questions.map((question, index) => {
                const isLayout = LAYOUT_TYPES.includes(question.type)
                // Compute question number (skip layout items)
                const questionNum = isLayout
                  ? null
                  : form.questions.slice(0, index).filter((q) => !LAYOUT_TYPES.includes(q.type))
                      .length + 1
                return (
                  <div
                    key={question.id}
                    className={`question-item ${selectedQuestionId === question.id ? 'selected' : ''} ${isLayout ? 'layout-item' : ''}`}
                    onClick={() => setSelectedQuestionId(question.id)}>
                    <div className="question-item-header">
                      <span className="question-number">{isLayout ? '' : questionNum}</span>
                      <span className="question-type-badge">
                        {QUESTION_TYPES.find((t) => t.type === question.type)?.icon}
                      </span>
                      <span className="question-label">
                        {question.label ||
                          (isLayout
                            ? question.type === 'divider'
                              ? 'Divider'
                              : 'Section Header'
                            : 'Untitled')}
                      </span>
                      {question.required && <span className="required-badge">*</span>}
                    </div>
                    <div className="question-item-actions">
                      <button
                        className="action-btn"
                        onClick={(e) => {
                          e.stopPropagation()
                          moveQuestion(question.id, 'up')
                        }}
                        disabled={index === 0}
                        title="Move up">
                        ↑
                      </button>
                      <button
                        className="action-btn"
                        onClick={(e) => {
                          e.stopPropagation()
                          moveQuestion(question.id, 'down')
                        }}
                        disabled={index === form.questions.length - 1}
                        title="Move down">
                        ↓
                      </button>
                      <button
                        className="action-btn"
                        onClick={(e) => {
                          e.stopPropagation()
                          duplicateQuestion(question.id)
                        }}
                        title="Duplicate">
                        ⧉
                      </button>
                      <button
                        className="action-btn action-delete"
                        onClick={(e) => {
                          e.stopPropagation()
                          deleteQuestion(question.id)
                        }}
                        title="Delete">
                        ✕
                      </button>
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </div>

        {/* Editor Panel */}
        <div className="editor-panel">
          {selectedQuestion ? (
            <QuestionEditor
              key={selectedQuestionId}
              question={selectedQuestion}
              onUpdate={(updates) => updateQuestion(selectedQuestion.id, updates)}
              surveyId={surveyId}
            />
          ) : (
            <div className="no-selection">
              <p>Select a question to edit</p>
              <p className="hint">Or add a new question to get started</p>
            </div>
          )}
        </div>

        {/* Preview Panel */}
        <div className="preview-panel">
          <div className="panel-header">
            <h3>Preview</h3>
          </div>
          <div className="preview-content">
            {brandImageUrl && (
              <div className="preview-brand">
                <img
                  src={brandImageUrl}
                  alt="Brand"
                  className="preview-brand-image"
                  onError={(e) => {
                    e.target.style.display = 'none'
                  }}
                />
              </div>
            )}
            {form.title && <h2 className="preview-title">{form.title}</h2>}
            {form.description && <p className="preview-description">{form.description}</p>}
            {form.questions.length === 0 ? (
              <div className="preview-empty">
                <p>Your survey preview will appear here</p>
              </div>
            ) : (
              <div className="preview-questions">
                {form.questions.map((question, index) => {
                  const isLayout = LAYOUT_TYPES.includes(question.type)
                  const questionNum = isLayout
                    ? null
                    : form.questions.slice(0, index).filter((q) => !LAYOUT_TYPES.includes(q.type))
                        .length + 1
                  return (
                    <QuestionPreview
                      key={question.id}
                      question={question}
                      index={questionNum !== null ? questionNum - 1 : index}
                      questionNumber={questionNum}
                      isSelected={selectedQuestionId === question.id}
                      onClick={() => setSelectedQuestionId(question.id)}
                      globalBgColor={form.questionBackgroundColor}
                    />
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
